import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTracking } from 'react-tracking';
import { useSnackbar } from '@/features/snackbar';

import { mutationFn } from './mutationFn';
import { AGGREGATE_PORTFOLIO_QUERY_KEY } from '@/hooks/portfolio/useAggregatePortfolio';

import type { AggregatePortfolioQueryClientData } from '@/hooks/portfolio/useAggregatePortfolio';
import { isSWSGQLError } from '@/utilities/graphQLClient';

const KNOWN_ERROR_CODES_MAP = {
  'PRTFL-004': 'Aggregate Portfolio Not Found. Please try again later.',
  'PRTFL-005':
    'Unable to update currency due to an error. Please try again later.',
} as const;

type Args = {
  onSuccess?: () => void;
};

export const useChangeAggregatePortfolioCurrency = ({ onSuccess }: Args) => {
  const queryClient = useQueryClient();
  const { addSnackbar } = useSnackbar();
  const { trackEvent } = useTracking();

  const mutation = useMutation({
    mutationFn,
    onSuccess: (aggregatePortfolio) => {
      trackEvent({
        action: 'success',
        modifier: 'changeAggregatePortfolioCurrency',
      });
      addSnackbar({
        type: 'positive',
        message: 'Currency has been changed successfully',
        lifeTime: 2000,
      });
      queryClient.setQueryData<AggregatePortfolioQueryClientData>(
        AGGREGATE_PORTFOLIO_QUERY_KEY,
        () => aggregatePortfolio
      );
      onSuccess?.();
    },
    onError: (e: unknown) => {
      let message = `Currency hasn't been changed due to an error. Please try again later.`;
      if (isSWSGQLError(e) && e.code in KNOWN_ERROR_CODES_MAP) {
        message = KNOWN_ERROR_CODES_MAP[e.code];
      }
      addSnackbar({
        type: 'negative',
        lifeTime: 2000,
        message,
      });
    },
  });

  return mutation;
};
